/* eslint-disable max-depth */
/* eslint-disable indent */
/* eslint-disable no-shadow */
/* eslint-disable complexity */
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { expandContract } from '@animations/expand-contract';
import { fadeIn } from '@animations/fade-in';
import { CrewHistoryComponent } from '@flight-details/crew/crew-history/crew-history.component';
import { CrewEntryResponse } from '@flight-details/crew/models/response/crew-entry.response';
import { CrewLocationView } from '@flight-details/crew/models/view/crew-location.view';
import {
  MsgType,
  NoContactDialogComponent,
} from '@shared/components/no-contact-dialog/no-contact-dialog.component';
import {
  OpenPhoneDialogComponent,
  OperationType,
} from '@shared/components/open-phone-dialog/open-phone-dialog.component';
import { SabreTextDialogComponent } from '@shared/components/sabre-text-dialog/sabre-text-dialog.component';
import { MemberLookupResponse } from '@shared/models/member-lookup.response';
import { RequestState } from '@shared/models/request-state';
import { AuthService } from '@shared/services/auth.service';
import { ConnectionStatusService } from '@shared/services/connection-status.service';
import { DecsStore } from '@shared/services/decs/decs.store';
import { MemberLookupService } from '@shared/services/member-lookup.service';
import { DateTime } from 'luxon';
import { Observable, tap } from 'rxjs';

interface CustomWindow extends Window {
  isOpenedInWebView?: boolean;
  openedFromApaPilot?: boolean;
  apaPilotVersion?: string;
  userTheme?: 'light' | 'dark';
  userFontSize?: 'small' | 'medium' | 'large';
}

// Make custom window properties accessible.
declare let window: CustomWindow;

enum ContactType {
  Phone,
  Text,
}

@Component({
  selector: 'app-crew-table',
  templateUrl: './crew-table.component.html',
  styleUrls: ['./crew-table.component.scss'],
  standalone: false,
  animations: [fadeIn, expandContract],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrewTableComponent {
  @Input() crewList?: CrewEntryResponse[];
  @Input() crewLocation?: CrewLocationView | null;
  @Input() showSeqNumbers?: boolean | null;
  @Input() crewType?: 'pilot' | 'fa' | 'dh';

  ContactType = ContactType; // Expose enum to template
  contactInfo$?: Observable<RequestState<MemberLookupResponse>>;
  groupTextInfo$?: Observable<RequestState<MemberLookupResponse[]>>;
  // This is used to identify which button was pressed to show a loading indicator.
  // Uses a combination of emp number and contact type, or 'group_text'.
  contactIsLoadingId: string | null = null;
  groupTextIsLoading = false;

  connectionStatus$ = this.connectionStatusService.connectionStatus$;

  constructor(
    private decsStore: DecsStore,
    private dialog: MatDialog,
    private authService: AuthService,
    private memberLookupService: MemberLookupService,
    private connectionStatusService: ConnectionStatusService,
  ) {}

  /** Show sequence Sabre text on sequence click. */
  onSeqClick(crewMember: CrewEntryResponse): void {
    const { sequenceNumber, sequenceDate, seat } = crewMember;

    if (sequenceNumber && sequenceDate && seat) {
      const date = DateTime.fromISO(sequenceDate, { zone: 'utc' }).toFormat(
        'ddMMM',
      );

      // Sabre command.
      const cmd = `hss/${seat}/${sequenceNumber}/${date}`;

      this.dialog.open(SabreTextDialogComponent, {
        data: {
          sabreText$: this.decsStore.submitDecsRequest(cmd, false),
          refreshFn: this.decsStore.refreshFn,
          request: cmd,
        },
        panelClass: 'sabre-text',
        maxHeight: '95vh',
        maxWidth: '100vw',
      });
    }
  }

  // onTrackClick(flightNumber?: string): void {
  //   if (flightNumber) {
  //     window.open(
  //       `https://flightaware.com/live/flight/AAL${flightNumber}`,
  //       '_blank',
  //     );
  //   }
  // }

  getFlightAwareLink(flightNumber: string): string {
    return `https://flightaware.com/live/flight/AAL${flightNumber}`;
  }

  /** Check if seat is only numeric, 'F/A' or emp num is > 6 (MQ) to determine pilot vs F/A vs Envoy. */
  isPilot(crewMember: CrewEntryResponse): boolean {
    if (
      crewMember.employeeNumber === '' ||
      /\d{2}/.test(crewMember?.seat) ||
      crewMember?.seat === 'F/A' ||
      crewMember.employeeNumber.length > 6
    ) {
      return false;
    }

    return true;
  }

  /** Is crewMember emp num equal to user's. */
  isUser(crewMember: CrewEntryResponse): boolean {
    return crewMember.employeeNumber === this.authService.empNumber;
  }

  onContactClick(pilot: CrewEntryResponse, type: ContactType): void {
    this.contactIsLoadingId = this.getContactId(pilot, type);

    this.contactInfo$ = this.memberLookupService
      .getContactInfo(pilot.employeeNumber)
      .pipe(
        tap((response) => {
          if (!response.isLoading) {
            this.contactIsLoadingId = null;
          }

          if (response.response) {
            const phoneNumber =
              response.response.cell?.trim().replace(/\D/g, '') ||
              response.response.telephone?.trim().replace(/\D/g, '');

            if (phoneNumber) {
              switch (type) {
                case ContactType.Phone:
                  this.dialog.open(OpenPhoneDialogComponent, {
                    data: {
                      type: OperationType.Phone,
                      code: `tel:${phoneNumber}`,
                    },
                  });
                  // window.open(`tel:${phoneNumber}`, '_');
                  break;
                case ContactType.Text:
                  this.dialog.open(OpenPhoneDialogComponent, {
                    data: {
                      type: OperationType.Text,
                      code: `sms:${phoneNumber}`,
                    },
                  });
                  // window.open(`sms:${phoneNumber}`, '_');
                  break;
                default:
              }
            } else {
              this.dialog.open(NoContactDialogComponent, {
                data: MsgType.Individual,
              });
            }
          }
        }),
      );
  }

  /** Only show group text button if there are at least two other pilots (not including user). */
  shouldShowGroupText(crewList: CrewEntryResponse[]): boolean {
    if (this.crewType !== 'pilot') {
      return false;
    }

    const usersEmpNum = this.authService.empNumber;
    let pilotCount = 0;

    for (const pilot of crewList) {
      if (pilot.employeeNumber !== usersEmpNum) {
        pilotCount++;
      }
    }

    return pilotCount > 1;
  }

  onGroupTextClick(crewList: CrewEntryResponse[]): void {
    const empNumbers = crewList.map((crew) => crew.employeeNumber);
    const empNumbersCount = empNumbers.length;

    this.groupTextInfo$ = this.memberLookupService
      .getMultipleContacts(empNumbers)
      .pipe(
        tap((response) => {
          if (response.isLoading) {
            this.groupTextIsLoading = true;
          } else {
            this.groupTextIsLoading = false;
            const phoneNumbers = response.response
              ?.map((pilot) => pilot.cell?.trim() || pilot.telephone?.trim())
              .filter((phoneNumber) => phoneNumber !== '');

            // List of names who don't have phone numbers available
            const noPhoneNames: string[] = [];

            if (!phoneNumbers || phoneNumbers.length < empNumbersCount) {
              // this.dialog.open(NoContactDialogComponent, {
              //   data: MsgType.GroupText,
              // });
              for (const empNum of empNumbers) {
                if (response.response) {
                  let found = false;
                  for (const lookupResult of response.response) {
                    if (
                      lookupResult.employeeNumber === empNum &&
                      (lookupResult.cell?.trim() ||
                        lookupResult.telephone?.trim())
                    ) {
                      found = true;
                    }
                  }

                  if (!found) {
                    // Find name of crew matching this emp num.
                    // Can't use member lookup response because it can be null.
                    for (const crew of crewList) {
                      if (empNum === crew.employeeNumber) {
                        noPhoneNames.push(
                          `${crew.nickname || crew.firstName} ${crew.name}`,
                        );
                      }
                    }
                  }
                }
              }
            }

            if (phoneNumbers) {
              // iOS seems to want a different format than Android
              const iOS = /iPhone|iPad|Mac/i.test(navigator.userAgent);
              let smsCommand = '';

              if (iOS && !window.openedFromApaPilot) {
                smsCommand = 'sms:open?addresses=';

                for (let i = 0; i < phoneNumbers.length; i++) {
                  if (i < phoneNumbers.length - 1) {
                    // Append comma
                    smsCommand += `${phoneNumbers[i]},`;
                  } else {
                    smsCommand += `${phoneNumbers[i]}`;
                  }
                }
              } else {
                smsCommand = 'sms:';

                for (let i = 0; i < phoneNumbers.length; i++) {
                  if (i < phoneNumbers.length - 1) {
                    // Append comma
                    smsCommand += `${phoneNumbers[i]},`;
                  } else {
                    smsCommand += `${phoneNumbers[i]}`;
                  }
                }
              }

              this.dialog.open(OpenPhoneDialogComponent, {
                data: {
                  type: OperationType.Text,
                  code: smsCommand,
                  noPhoneNames,
                },
              });
              // window.open(smsCommand, '_');
            }
          }
        }),
      );
  }

  getContactId(pilot: CrewEntryResponse, type: ContactType): string {
    return `${pilot.employeeNumber}-${type}`;
  }

  contactIsLoading(pilot: CrewEntryResponse, type: ContactType): boolean {
    return this.contactIsLoadingId === this.getContactId(pilot, type);
  }

  onHistoryClick(pilot: CrewEntryResponse): void {
    this.dialog.open(CrewHistoryComponent, {
      data: {
        pilot,
      },
      maxHeight: '90vh',
      maxWidth: '100vw',
    });
  }

  /** Returns sms attribute string for group text. Omits pilot if he/she is in list.  Returns null if there are fewer than 3 pilots. */
  getGroupSmsText(crewList: CrewEntryResponse[]): null | string {
    if (this.crewType !== 'pilot') {
      return null;
    }

    const phoneNumbers: string[] = [];

    // Get phone numbers
    for (const pilot of crewList) {
      // Don't include pilot's own phone number
      if (pilot.employeeNumber !== this.authService.empNumber) {
        if (pilot.contact?.mobilePhone) {
          phoneNumbers.push(pilot.contact?.mobilePhone);
        } else if (pilot.contact?.homePhone) {
          phoneNumbers.push(pilot.contact?.homePhone);
        }
      }
    }

    // Only create group text link if there are at least 2 other pilots with phone numbers.
    if (phoneNumbers.length > 1) {
      // iOS seems to want a different format than Android
      const iOS = /iPhone|iPad|Mac/i.test(navigator.userAgent);

      if (iOS) {
        let smsCommand = 'sms:open?addresses=';

        for (let i = 0; i < phoneNumbers.length; i++) {
          if (i < phoneNumbers.length - 1) {
            // Append comma
            smsCommand += `+1${phoneNumbers[i]},`;
          } else {
            smsCommand += `+1${phoneNumbers[i]}`;
          }
        }

        return `${smsCommand}`;
      } else {
        let smsCommand = 'sms:';

        for (let i = 0; i < phoneNumbers.length; i++) {
          if (i < phoneNumbers.length - 1) {
            // Append comma
            smsCommand += `+1${phoneNumbers[i]},`;
          } else {
            smsCommand += `+1${phoneNumbers[i]}`;
          }
        }

        return smsCommand;
      }
    } else {
      return null;
    }
  }
}
